const CHECK_INTERVAL = 60000;
let requestsInFlight = 0;
let runtime = null;
let state = { version: null, canReload: false, canPrompt: false, reason: null, reloading: false };
const listeners = new Set();

export const getFrontendUpdateState = () => state;
export const subscribeToFrontendUpdates = (listener) => {
  listeners.add(listener);
  listener(state);
  return () => listeners.delete(listener);
};
export const reloadFrontendForUpdate = () => runtime ? runtime.confirm() : false;
const notify = (next) => {
  if (JSON.stringify(state) === JSON.stringify(next)) return;
  state = next;
  listeners.forEach((listener) => listener(state));
};
export function beginUpdateRequest() {
  requestsInFlight += 1;
  if (runtime) runtime.publish();
  let finished = false;
  return () => {
    if (!finished) requestsInFlight -= 1;
    finished = true;
    if (runtime) runtime.publish();
  };
}
const editable = (element) => element && element.matches && element.matches(
  'input:not([type="button"]):not([type="submit"]):not([type="reset"]), select, textarea, [contenteditable="true"]'
);
const fieldValue = (element) => {
  if (element.type === 'checkbox' || element.type === 'radio') return element.checked;
  return element.matches('[contenteditable="true"]') ? element.textContent : element.value;
};
export function currentFrontendAssets(doc = document) {
  return Array.from(doc.querySelectorAll('script[src], link[rel="stylesheet"][href]'))
    .map((element) => new URL(element.getAttribute('src') || element.getAttribute('href'), doc.baseURI))
    .filter((url) => url.origin === window.location.origin && /\/static\/(js|css)\//.test(url.pathname))
    .map((url) => url.pathname).sort();
}

// Detection never reloads. Only the explicit confirmation can reload, and it
// rechecks current editing/request state at the time the user presses Update.
export function startFrontendUpdates({
  doc = document, win = window,
  fetchVersion = (signal) => fetch(`${process.env.PUBLIC_URL || ''}/frontend-version.json`, {
    cache: 'no-store', credentials: 'omit', signal,
  }),
  reload = () => window.location.reload(), assets = currentFrontendAssets(doc),
} = {}) {
  const originalValues = new WeakMap();
  const dirtyFields = new Map();
  const loaded = JSON.stringify(assets.slice().sort());
  let pending = null;
  let checking = false;
  let stopped = false;
  let reloading = false;
  let activeCheck;
  const publish = () => {
    if (stopped) return;
    for (const [field, pathname] of dirtyFields) {
      if (!field.isConnected && win.location.pathname !== pathname) dirtyFields.delete(field);
    }
    const otherDialog = !!doc.querySelector('.modal.show:not(.frontend-update-modal), dialog[open], [role="dialog"][aria-modal="true"]:not(.frontend-update-modal)');
    const reason = reloading ? 'updating' : requestsInFlight > 0 ? 'request'
      : dirtyFields.size > 0 ? 'edits' : otherDialog ? 'dialog' : null;
    notify({ version: pending, canReload: !!pending && !reason && doc.visibilityState === 'visible',
      canPrompt: doc.visibilityState === 'visible' && !otherDialog && !editable(doc.activeElement), reason, reloading });
  };
  const confirm = () => {
    publish();
    if (stopped || !state.canReload) return false;
    reloading = true;
    publish();
    reload();
    return true;
  };
  runtime = { confirm, publish };
  const remember = (event) => {
    if (editable(event.target) && !originalValues.has(event.target)) originalValues.set(event.target, fieldValue(event.target));
  };
  const changed = (event) => {
    const field = event.target;
    if (!editable(field)) return;
    if (!originalValues.has(field) || fieldValue(field) !== originalValues.get(field)) dirtyFields.set(field, win.location.pathname);
    else dirtyFields.delete(field);
    publish();
  };
  const check = async () => {
    publish();
    if (stopped || checking || reloading || doc.visibilityState !== 'visible') return;
    checking = true;
    const controller = new AbortController();
    activeCheck = controller;
    const timeout = win.setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetchVersion(controller.signal);
      if (!response.ok) return;
      const version = await response.json();
      if (stopped || typeof version.id !== 'string' || !version.id
        || !Array.isArray(version.assets) || version.assets.length === 0
        || version.assets.some((asset) => typeof asset !== 'string' || !/^\/static\/(js|css)\//.test(asset))) return;
      pending = JSON.stringify(version.assets.slice().sort()) === loaded ? null : version;
      publish();
    } catch { /* Offline/invalid responses never interrupt the current page. */ }
    finally { win.clearTimeout(timeout); activeCheck = null; checking = false; }
  };
  doc.addEventListener('focusin', remember, true);
  doc.addEventListener('input', changed, true);
  doc.addEventListener('change', changed, true);
  doc.addEventListener('visibilitychange', check);
  win.addEventListener('focus', check);
  win.addEventListener('online', check);
  const checkTimer = win.setInterval(check, CHECK_INTERVAL);
  const stateTimer = win.setInterval(publish, 1000);
  check();
  return () => {
    stopped = true;
    if (activeCheck) activeCheck.abort();
    runtime = null;
    notify({ version: null, canReload: false, canPrompt: false, reason: null, reloading: false });
    win.clearInterval(checkTimer); win.clearInterval(stateTimer);
    doc.removeEventListener('focusin', remember, true);
    doc.removeEventListener('input', changed, true);
    doc.removeEventListener('change', changed, true);
    doc.removeEventListener('visibilitychange', check);
    win.removeEventListener('focus', check); win.removeEventListener('online', check);
  };
}
